import { useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { Popover, rectOf, type AnchorRect } from '@/ui/Popover';
import { Icon } from '@/ui/icons';

export interface SearchOption {
  value: string;
  label: string;
  /** A heading the option sits under — "Baked sections". Consecutive options share it. */
  group?: string;
}

/**
 * A select with a search box.
 *
 * For every list of Giraffe fields or values. A project carries a hundred-odd
 * properties and a layer list can run to dozens of ids, and a native select
 * makes you scroll for them; this matches what you type anywhere in the label
 * or the value.
 *
 * It answers to POINTERDOWN and stops propagation, like the chip menu: the
 * popover is a portal, so inside the grid's React tree a press would otherwise
 * bubble to the grid and select a cell under the menu.
 */
export function SearchSelect({
  value,
  options,
  onChange,
  placeholder = 'Choose…',
  title,
  className,
  allowCustom = false,
  multiValues,
  onChangeMulti,
}: {
  value: string;
  options: readonly SearchOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  title?: string;
  className?: string;
  /** Offer what was typed as a value of its own — for `name~tower`, `levels>3`. */
  allowCustom?: boolean;
  /**
   * When set, picking toggles membership in this list instead of choosing
   * one value and closing — `value`/`onChange` are ignored entirely. Every
   * existing caller leaves this unset and is unaffected.
   */
  multiValues?: readonly string[];
  onChangeMulti?: (values: string[]) => void;
}): JSX.Element {
  const isMulti = multiValues !== undefined;
  const [at, setAt] = useState<AnchorRect | null>(null);
  const ref = useRef<HTMLButtonElement | null>(null);
  const current = options.find((o) => o.value === value);
  const label = isMulti
    ? multiValues.length === 0
      ? placeholder
      : multiValues.length === 1
        ? (options.find((o) => o.value === multiValues[0])?.label ?? multiValues[0])
        : `${multiValues.length} selected`
    : (current?.label ?? (value || placeholder));
  return (
    <>
      <button
        ref={ref}
        type="button"
        className={`gt-select gt-ss${className ? ` ${className}` : ''}`}
        title={title ?? (isMulti ? undefined : current?.label)}
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAt(at ? null : rectOf(ref.current));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            setAt(rectOf(ref.current));
          }
        }}
      >
        <span className="gt-ss-label">{label}</span>
        <span className="gt-ss-caret">▾</span>
      </button>
      {at ? (
        <SearchList
          anchor={at}
          options={options}
          value={value}
          multiValues={multiValues}
          allowCustom={allowCustom}
          onPick={(v) => {
            if (isMulti) {
              const next = new Set(multiValues);
              if (next.has(v)) next.delete(v);
              else next.add(v);
              onChangeMulti?.([...next]);
              return; // stays open — picking more is the point
            }
            setAt(null);
            onChange(v);
          }}
          onClose={() => setAt(null)}
        />
      ) : null}
    </>
  );
}

function SearchList({
  anchor,
  options,
  value,
  multiValues,
  allowCustom,
  onPick,
  onClose,
}: {
  anchor: AnchorRect;
  options: readonly SearchOption[];
  value: string;
  multiValues?: readonly string[];
  allowCustom: boolean;
  onPick: (value: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => {
    // The label, and the bare field name — `layerId` finds "Layer Id". Not the
    // whole value: a key like `usages\u0000color` would match every field of
    // a source by the source's name.
    const bare = (v: string): string => (v.split('\u0000').pop() ?? v).toLowerCase();
    const hits = options.filter((o) => q === '' || o.label.toLowerCase().includes(q) || bare(o.value).includes(q));
    // What was typed, as a value of its own, when nothing matches it exactly.
    if (allowCustom && q !== '' && !options.some((o) => o.value.toLowerCase() === q)) {
      return [{ value: query.trim(), label: `Use "${query.trim()}"` }, ...hits];
    }
    return hits;
  }, [options, q, query, allowCustom]);
  const stop = (e: React.SyntheticEvent): void => e.stopPropagation();
  const input = useRef<HTMLInputElement | null>(null);
  // Not autoFocus: the popover is hidden until it has measured where to sit,
  // and a hidden input refuses focus — the typing went to the grid instead.
  useEffect(() => {
    const raf = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <Popover anchor={anchor} onClose={onClose} className="gt-menu-panel gt-ss-panel" swallowOutsidePress>
      <div onPointerDown={stop}>
        <input
          ref={input}
          className="gt-input gt-chip-search"
          placeholder={`Search ${options.length}…`}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(0);
          }}
          onKeyDown={(e) => {
            // The grid's key handler is an ancestor in the React tree; without
            // this, typing here also types into the cell underneath.
            e.stopPropagation();
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setCursor((c) => Math.min(shown.length - 1, c + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setCursor((c) => Math.max(0, c - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const pick = shown[cursor];
              if (pick) onPick(pick.value);
            }
          }}
        />
        <div className="gt-chip-options">
          {shown.map((o, i) => (
            <div key={`${o.group ?? ''}\u0000${o.value}`}>
              {o.group && o.group !== shown[i - 1]?.group ? <div className="gt-menu-heading">{o.group}</div> : null}
              <div
                role="option"
                aria-selected={multiValues ? multiValues.includes(o.value) : o.value === value}
                className={`gt-menu-row gt-ss-option${i === cursor ? ' is-cursor' : ''}${(multiValues ? multiValues.includes(o.value) : o.value === value) ? ' is-checked' : ''}`}
                onPointerEnter={() => setCursor(i)}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onPick(o.value);
                }}
              >
                {multiValues ? (
                  <span className="gt-ss-check">{multiValues.includes(o.value) ? <Icon.check /> : null}</span>
                ) : null}
                <span className="gt-menu-label">{o.label}</span>
              </div>
            </div>
          ))}
          {shown.length === 0 ? <div className="gt-menu-note">No match</div> : null}
        </div>
      </div>
    </Popover>
  );
}
